import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {Bookmark, Delivery, deliveryFromBookmark, getOccupancyDeliveries} from "./types";
import {Occupancy} from "@variocube/app-api";
import {GridItem, GridViewContent} from "./grid";
import {classNames, layout, PushButton, theme, typography, usePlatform, ViewContent} from "@variocube/cube-ui";
import {stylesheet} from "typestyle";
import {useLocalization} from "../i18n";

interface BookmarksListProps {
    bookmarks: Bookmark[];
    occupancies: Occupancy[];
    onOpen: (delivery: Delivery, bookmark: Bookmark) => void;
}

interface DeliveryOccupancy {
    occupancy: Occupancy;
    delivery?: Delivery;
}

export function BookmarksList(props: BookmarksListProps) {
    const {bookmarks, occupancies, onOpen} = props;

    const [bookmark, setBookmark] = useState<Bookmark | undefined>();

    const allDeliveries = useMemo<DeliveryOccupancy[]>(() => {
        return occupancies.flatMap((occupancy) => {
            const deliveries = getOccupancyDeliveries(occupancy) ?? [];
            return deliveries.map(delivery => ({occupancy: occupancy, delivery}));
        }).filter((b) => b.delivery != undefined);
    }, [occupancies]);

    if(bookmark) {
        return (<BookmarkView bookmark={bookmark} occupancies={allDeliveries} onClose={() => setBookmark(undefined)} onOpen={(d) => onOpen(d, bookmark)} />);
    }

    return (
        <GridViewContent>
            {filterBookmarkCubes(bookmarks).filter(x=>x.description).map((b) =>
                <GridItem key={b.id} label={b.label} onClick={() => setBookmark(b)}>
                    <BookmarkCard key={b.id} bookmark={b} />
                </GridItem>
            )}
        </GridViewContent>
    );
}

export const filterBookmarkCubes = (bookmarks: Bookmark[]) => {
    const platform = usePlatform();

    return bookmarks.filter(item=>item.cubeId == platform.hostname || !item.cubeId);
}

interface BookmarkCardProps {
    bookmark: Bookmark;
}

function BookmarkCard(props: BookmarkCardProps) {
    const {bookmark} = props;

    return (
        <div>{bookmark.description}</div>
    );
}

interface BookmarkViewProps {
    bookmark: Bookmark;
    occupancies: DeliveryOccupancy[];
    onClose: () => void;
    onOpen: (delivery: Delivery) => void;
}

function BookmarkView(props: BookmarkViewProps) {
    const {bookmark, occupancies, onClose, onOpen} = props;

    const platform = usePlatform();

    const filteredOccupancies = useMemo<DeliveryOccupancy[]>(() =>
        occupancies.filter((o) => o.delivery && o.delivery.bookmark == bookmark.id), [bookmark, occupancies]);

    const handleStore = useCallback(() => {
        if(filteredOccupancies && filteredOccupancies.length > 0) {
            const first = filteredOccupancies[0];
            if(first.delivery) {
                platform.openBoxLock(first.occupancy.boxNumber);
                onOpen(first.delivery);
                return;
            }
        }
        const newDelivery = deliveryFromBookmark(bookmark);
        onOpen(newDelivery);
    }, [bookmark, filteredOccupancies, onClose]);

    const {t} = useLocalization();

    return (
        <ViewContent className={classNames(layout.vertical, layout.center, layout.alignCenter)}>
            <div className={classNames(layout.horizontal, styles.marginTop)}>
                <div className={classNames(theme.moreContrast, layout.vertical, styles.boxLeft, layout.paddingMedium)}>
                    <div className={typography.title}>
                        {bookmark.label}
                    </div>
                    <div className={typography.description}>
                        {bookmark.description}
                    </div>
                </div>
                <div className={classNames(theme.lessContrast, layout.vertical, styles.boxRight)}>
                    <div className={classNames(layout.horizontal, layout.paddingMedium)}>
                        <PushButton className={classNames(styles.action)} onClick={() => handleStore()} primary>{t("Store")}</PushButton>
                    </div>
                    <div className={classNames(layout.horizontal, layout.paddingMedium)}>
                        <PushButton className={classNames(styles.action)} onClick={() => onClose()}>{t("Cancel")}</PushButton>
                    </div>
                </div>
            </div>
        </ViewContent>
    );
}

const styles = stylesheet({
    marginTop: {
        marginTop: "2em"
    },
    boxLeft: {
        width: "24em"
    },
    boxRight: {
        width: "20em",
        padding: "1.5em"
    },
    action: {
        margin: "auto",
        cursor: "pointer"
    },
    bookmark: {
        maxWidth: "90%"
    }
});